import { lazy, Suspense, useEffect, useRef } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SiteProvider } from "./context/SiteContext";
import { CartProvider } from "./context/CartContext";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import MiniCart from "./components/layout/MiniCart";
import ChatWidget from "./components/layout/ChatWidget";
import { MobileBar, NewsletterPopup, CookieBanner } from "./components/layout/Extras";
import { Spinner } from "./components/common";
import { api, visitorId } from "./lib/api";
import { lp, startTranslator } from "./lib/i18n";
import { legacyTarget } from "./lib/routes";
import { useSeo } from "./lib/seo";
import Home from "./pages/Home";

const Catalog = lazy(() => import("./pages/Catalog"));
const Product = lazy(() => import("./pages/Product"));
const Packs = lazy(() => import("./pages/Packs"));
const Services = lazy(() => import("./pages/Services"));
const Blog = lazy(() => import("./pages/Blog"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Login = lazy(() => import("./pages/Login"));
const Account = lazy(() => import("./pages/Account"));
const Admin = lazy(() => import("./admin/AdminApp"));

function AuthCallback() {
  const done = useRef(false);
  const nav = useNavigate();
  const { setUser } = useAuth();
  const location = useLocation();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data); window.history.replaceState(null, "", lp("/compte")); nav(lp("/compte"), { replace: true }); })
      .catch(() => nav(lp("/connexion"), { replace: true }));
  }, []); // eslint-disable-line
  return <Spinner className="min-h-screen" />;
}

function Tracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    let src = sessionStorage.getItem("mb_src");
    if (!src) {
      const u = new URLSearchParams(window.location.search).get("utm_source");
      src = u || (document.referrer && !document.referrer.includes(window.location.host) ? new URL(document.referrer).hostname : "direct");
      sessionStorage.setItem("mb_src", src);
    }
    if (!pathname.startsWith("/admin")) api.post("/track", { path: pathname, source: src, visitor_id: visitorId() }).catch(() => {});
  }, [pathname]);
  return null;
}

function NotFound() {
  const { pathname, search } = useLocation();
  const target = legacyTarget(pathname);
  useSeo({ title: "Strona nie istnieje", noindex: true });
  if (target) return <Navigate to={target + search} replace />;
  return (
    <div className="container-x max-w-xl py-24 text-center" data-testid="not-found-page">
      <p className="eyebrow">404</p>
      <h1 className="mt-3 text-4xl font-semibold">Strona nie istnieje</h1>
      <p className="mt-4 text-pierre">Ta strona została usunięta lub nigdy nie istniała.</p>
      <a href="/sklep" className="btn-primary mt-8 inline-flex" data-testid="not-found-shop-link">Przejdź do sklepu</a>
    </div>
  );
}

function Shop() {
  const { pathname } = useLocation();
  return (
    <>
      <Header />
      <main className="min-h-[60vh]">
        <ErrorBoundary resetKey={pathname}>
        <Suspense fallback={<Spinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/sklep" element={<Catalog />} />
            <Route path="/sklep/:category" element={<Catalog />} />
            <Route path="/piece" element={<Catalog universe="poeles" />} />
            <Route path="/pellet" element={<Catalog universe="granules" />} />
            <Route path="/akcesoria" element={<Catalog universe="accessoires" />} />
            <Route path="/ulubione" element={<Catalog favorites />} />
            <Route path="/zestawy" element={<Packs />} />
            <Route path="/produkt/:slug" element={<Product />} />
            <Route path="/kalkulator" element={<Services page="calculateur" />} />
            <Route path="/dostawa" element={<Services page="livraison" />} />
            <Route path="/montaz" element={<Services page="installation" />} />
            <Route path="/wycena" element={<Services page="devis" />} />
            <Route path="/kontakt" element={<Services page="contact" />} />
            <Route path="/pomoc" element={<Services page="aide" />} />
            <Route path="/o-nas" element={<Services page="page" slug="a-propos" />} />
            <Route path="/informacje/:slug" element={<Services page="page" />} />
            <Route path="/porady" element={<Blog />} />
            <Route path="/porady/:slug" element={<Blog />} />
            <Route path="/koszyk" element={<Cart />} />
            <Route path="/zamowienie" element={<Checkout />} />
            <Route path="/zamowienie/potwierdzenie/:id" element={<Checkout confirmation />} />
            <Route path="/platnosc/sukces" element={<Checkout paymentSuccess />} />
            <Route path="/logowanie" element={<Login />} />
            <Route path="/reset-hasla" element={<Login reset />} />
            <Route path="/konto" element={<Account />} />
            <Route path="/konto/:tab" element={<Account />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
      <MiniCart />
      <ChatWidget />
      <MobileBar />
      <NewsletterPopup />
      <CookieBanner />
    </>
  );
}

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Tracker />
      <Routes>
        <Route path="/admin/*" element={<ErrorBoundary><Suspense fallback={<Spinner />}><Admin /></Suspense></ErrorBoundary>} />
        <Route path="/*" element={<Shop />} />
      </Routes>
    </>
  );
}

export default function App() {
  useEffect(() => { startTranslator(); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteProvider>
          <CartProvider>
            <Router />
            <Toaster position="top-center" richColors />
          </CartProvider>
        </SiteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
